import { ref, computed } from 'vue'

/**
 * 集团页面亮/暗主题切换。
 *
 * 仅作用于「集团」菜单下、已完成重构的页面（见 GROUP_THEME_ROUTES）。
 * 集团菜单外的页面以及尚未重构的集团页面保持原有暗色，不受影响。
 *
 * 配置都集中在本文件，页面不再各自覆盖：
 *  - 进入页面的默认主题：改下方 DEFAULT_MODE 一处即可（当前：暗色）。
 *  - 是否显示亮/暗切换按钮：改下方 SHOW_THEME_TOGGLE 一处即可（当前：隐藏）。
 */

export type GroupThemeMode = 'light' | 'dark'

/** 进入集团重构页时的默认主题（改这里即可，'light' | 'dark'） */
export const DEFAULT_MODE: GroupThemeMode = 'dark'

/** 是否在集团重构页显示亮/暗切换按钮（改这里即可控制所有集团页） */
export const SHOW_THEME_TOGGLE = false

/**
 * 已完成亮色重构、参与主题切换的集团路由 name 集合。
 * 后续每重构完一个集团页面，把它的路由 name 加进来即可。
 */
export const GROUP_THEME_ROUTES = new Set<string>([
  'output-report',
  'design-report',
  'estimate-report',
  'estimate-overview',
])

export function isGroupThemeRoute(name: unknown): boolean {
  return typeof name === 'string' && GROUP_THEME_ROUTES.has(name)
}

// 模块级单例，保证 App.vue 与各集团页面共享同一份状态；默认取 DEFAULT_MODE
const mode = ref<GroupThemeMode>(DEFAULT_MODE)

export function useGroupTheme() {
  const isLight = computed(() => mode.value === 'light')

  function setMode(m: GroupThemeMode) {
    mode.value = m
  }

  function toggle() {
    setMode(mode.value === 'light' ? 'dark' : 'light')
  }

  return { mode, isLight, setMode, toggle, showToggle: SHOW_THEME_TOGGLE }
}
